// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  EmbeddedViewRef,
  Input,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { SharedModule } from 'src/app/modules/shared/shared.module';
import { DnaSyngoviaScoreSeverityEtype } from '../../enum/dna-syngovia-score-severity-type';
import { IssuesResponsibilityCategoryEType } from '../../enum/issues-responsibility-category-type';
import { DnaSyngoviaHealthcheckIssue } from '../../interfaces/dna-syngovia-healthcheck-data';
import {
  DnaSyngoviaDateRangeType,
  DnaSyngoviaIssuesFilterModel,
  DnaSyngoviaScoreRangeType,
} from '../../model/dna-syngovia-issues-filter-model';
import { DnaSyngoviaScoreSeverityService } from '../../services/dna-syngovia-score-severity.service';
import { DnaSyngoviaScrollPaginationModel } from '../../model/dna-syngovia-scroll-pagination-model';
import { UserService } from 'src/app/modules/portal/services/user.service';
import { DnaSyngoviaService } from '../../services/dna-syngovia.service';
import { DnaSyngoviaServerNode } from '../../interfaces/dna-syngovia-node';
import { SystemOverviewService } from 'src/app/modules/portal/services/systemOverview.service';

export type TagTemplateContextType = {
  $implicit: {
    value: string | DnaSyngoviaDateRangeType;
    type: 'date' | 'string';
    closeFn: () => void;
    clearFilterCallback: () => void;
  };
};

@Component({
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    SharedModule
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: 'app-dna-syngovia-healthcheck-issues-table',
  templateUrl: './dna-syngovia-healthcheck-issues-table.component.html',
  styleUrls: ['./dna-syngovia-healthcheck-issues-table.component.scss'],
})
export class DnaSyngoviaHealthcheckIssuesTableComponent
  implements OnChanges, OnDestroy
{
  @Input({ required: true }) issuesIdentifiedAt: string = '';
  @Input({ required: true }) issues: DnaSyngoviaHealthcheckIssue[] = [];
  @Input({ required: true }) serialNumber: string = '';
  @Input({ required: true }) materialNumber: number = 0;
  @ViewChild('popover') popoverElementRef: ElementRef | undefined;
  @ViewChild('searchInput') searchInput: ElementRef | undefined;

  @ViewChild('tagViewRef', { read: ViewContainerRef }) viewRef:
    | ViewContainerRef
    | undefined;

  @ViewChild('tag') tagTemplateRef:
    | TemplateRef<TagTemplateContextType>
    | undefined;

  protected tagsRefs: EmbeddedViewRef<TagTemplateContextType>[] = [];
  private readonly searchSubject = new Subject<string>();
  private readonly unsubscribe = new Subject<void>();
  protected readonly NOT_FOUND = '-';

  protected isSortDescending = true;

  protected issuesFilterModel = new DnaSyngoviaIssuesFilterModel();
  protected scrollPaginationModel = new DnaSyngoviaScrollPaginationModel();

  protected filteredIssues: DnaSyngoviaHealthcheckIssue[] = [];

  protected responsibleDomainValues = [
    { label: 'customer', value: IssuesResponsibilityCategoryEType.CUSTOMER },
    { label: 'rsc', value: IssuesResponsibilityCategoryEType.RSC },
    { label: 'unknown', value: IssuesResponsibilityCategoryEType.UNKNOWN },
  ];
  protected scoreRangeDomainValues = [
    { label: 'rangeLow', value: DnaSyngoviaScoreSeverityEtype.LOW },
    { label: 'rangeMedium', value: DnaSyngoviaScoreSeverityEtype.MEDIUM },
    { label: 'rangeHigh', value: DnaSyngoviaScoreSeverityEtype.HIGH },
  ];

  private readonly networkConnections =
    this.dnaSyngoviaService.getNetworkConnections;

  protected readonly srsConnectivity =
    this.systemOverviewService.srsConnectivity.lastSuccessfulData$;

  protected serverNodes: DnaSyngoviaServerNode[] = [];

  constructor(
    protected readonly dnaSyngoviaScoreSeverityService: DnaSyngoviaScoreSeverityService,
    protected readonly systemOverviewService: SystemOverviewService,
    protected readonly userService: UserService,
    protected readonly dnaSyngoviaService: DnaSyngoviaService,
    private readonly renderer: Renderer2,
  ) {
    this.listenToSearchSubject();
    this.listenToNetworkConnections();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.searchInput) {
      this.renderer.setProperty(this.searchInput.nativeElement, 'value', '');
    }
    if (changes['issues']) {
      this.clearTags();
      this.issuesFilterModel = new DnaSyngoviaIssuesFilterModel();

      this.updatePaginationModel(this.issues);
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  private listenToNetworkConnections(): void {
    this.networkConnections.lastSuccessfulData$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((connections) => {
        if (connections.serverGroup) {
          this.serverNodes = connections.serverGroup
            .nodes as unknown as DnaSyngoviaServerNode[];
        }
      });
  }

  protected onTableScrollEnd(): void {
    this.scrollPaginationModel.onScrollEnd();
  }

  private updatePaginationModel(elements: DnaSyngoviaHealthcheckIssue[]): void {
    this.scrollPaginationModel.totalElements = elements.length;
    this.scrollPaginationModel.pageIndex = 0;
    this.scrollPaginationModel.updatePageFn = (start: number, end: number) => {
      this.filteredIssues.push(...elements.slice(start, end));
    };

    this.filteredIssues = elements.slice(
      0,
      this.scrollPaginationModel.pageSize,
    );
  }

  protected toggleSort<T extends keyof DnaSyngoviaHealthcheckIssue>(
    property: T,
    isDescending = false,
  ): void {
    this.issues.sort((a, b) => {
      let valueA: DnaSyngoviaHealthcheckIssue[T] | string = a[property];
      let valueB: DnaSyngoviaHealthcheckIssue[T] | string = b[property];

      if (property === 'skb') {
        valueA = a.skb?.description;
        valueB = b.skb?.description;
      }
      if (!valueA || !valueB) {
        return 0;
      }
      if (valueA < valueB) {
        return isDescending ? 1 : -1;
      }
      if (valueA > valueB) {
        return isDescending ? -1 : 1;
      }
      return 0;
    });
    this.isSortDescending = isDescending;
    this.dispatchSearch();
  }

  private updateTags(): void {
    this.clearTags();

    for (const key of Object.keys(this.issuesFilterModel.scoreRange)) {
      const property = key as keyof DnaSyngoviaScoreRangeType;
      const obj = this.issuesFilterModel.scoreRange[property];
      if (obj.active) {
        this.addTag(obj.label, () => {
          obj.active = false;
          this.dispatchSearch();
        });
      }
    }

    this.dispatchSearch();
  }

  private dispatchSearch() {
    if (this.searchInput) {
      const searchValue = this.searchInput.nativeElement.value ?? '';
      this.searchSubjectSubscribeFn(searchValue);
    }
  }

  private clearTags(): void {
    this.tagsRefs = [];
    this.viewRef?.clear();
  }

  protected updateScoreRangeFilter<T extends keyof DnaSyngoviaScoreRangeType>(
    property: T,
    active: boolean,
    label: string,
  ): void {
    const scoreRange = this.issuesFilterModel.scoreRange;
    scoreRange[property].active = active;
    scoreRange[property].label = label;

    this.updateTags();
  }

  private onCloseTag(tagRef: EmbeddedViewRef<TagTemplateContextType>) {
    return () => {
      const context = tagRef.context.$implicit;
      context.clearFilterCallback();

      const tagIndex = this.tagsRefs.indexOf(tagRef);
      if (tagIndex !== -1) {
        this.removeTagView(tagIndex);
      }
    };
  }

  private readonly addTag = (
    value: string | DnaSyngoviaDateRangeType,
    clearFilterCallback: () => void,
    type: 'date' | 'string' = 'string',
  ) => {
    if (this.viewRef && this.tagTemplateRef) {
      const tagRef = this.viewRef.createEmbeddedView(this.tagTemplateRef);

      tagRef.context.$implicit = {
        value,
        type,
        closeFn: this.onCloseTag(tagRef),
        clearFilterCallback,
      };

      this.tagsRefs.push(tagRef);
    }
  };

  private removeTagView(tagIndex: number) {
    this.tagsRefs.splice(tagIndex, 1);
    this.viewRef?.remove(tagIndex);
  }

  protected onInputChange(searchInput: string): void {
    this.searchSubject.next((searchInput ?? '').trim());
  }

  private listenToSearchSubject(): void {
    this.searchSubject
      .pipe(debounceTime(300), takeUntil(this.unsubscribe))
      .subscribe(this.searchSubjectSubscribeFn);
  }

  private readonly searchSubjectSubscribeFn = (searchInput: string) => {
    if (searchInput !== undefined && searchInput !== null) {
      const filtered = this.issues
        .filter(this.filterByScoreSeverity)
        .filter(this.filterBySearchInput(searchInput));

      this.updatePaginationModel(filtered);
    }
  };

  private readonly filterByScoreSeverity = (
    issue: DnaSyngoviaHealthcheckIssue,
  ): boolean => {
    let result = true;
    if (issue.score !== undefined && issue.score !== null) {
      const scoreSeverity = this.dnaSyngoviaScoreSeverityService.scoreSeverity(
        issue.score,
      );
      result = this.issuesFilterModel.filterScoreRange(scoreSeverity);
    }

    return result;
  };

  private filterBySearchInput(searchInput: string) {
    return (issue: DnaSyngoviaHealthcheckIssue): boolean => {
      let result = true;
      if (searchInput !== undefined && searchInput !== null) {
        const description = (issue.description ?? '').toLowerCase();
        const category = (issue.category ?? '').toLowerCase();
        const issueId = (issue.issueId ?? '').toLowerCase();
        const skbDescription = (issue.skb?.description ?? '').toLowerCase();

        const sInput = searchInput.toLowerCase();

        result =
          description.includes(sInput) ||
          category.includes(sInput) ||
          issueId.includes(sInput) ||
          skbDescription.includes(sInput);
      }

      return result;
    };
  }

  protected onSkbClick(link: string): void {
    window.open(link, '_blank');
  }
}
